























































































































































































































import Vue, { PropType } from 'vue';
import { WithRefs } from 'vue-typed-refs';
import { nanoid } from 'nanoid';
import debounce from 'lodash.debounce';
import { createPopper } from '@popperjs/core';

import SldsIcon from '../Icon/Icon.vue';
import SldsInputIcon from '../InputIcon/InputIcon.vue';
import SldsFormElement from '../FormElement/FormElement.vue';
import SldsSpinner from '../Spinner/Spinner.vue';

import { ComboBoxItem, ComboBoxCreateEventPayload } from './types';

type Refs = {
  root: HTMLElement;
  input: HTMLInputElement;
  listbox: HTMLElement;
};

function isScrolledToBottom(element: HTMLElement) {
  return element.scrollHeight - element.scrollTop === element.clientHeight;
}

export default (Vue as WithRefs<Refs>).extend({
  components: {
    SldsIcon,
    SldsInputIcon,
    SldsFormElement,
    SldsSpinner,
  },
  props: {
    uid: {
      type: String,
      default() {
        return nanoid();
      },
    },
    value: {
      type: [Array, String],
      default: undefined,
    },
    label: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    items: {
      type: Array as PropType<ComboBoxItem[]>,
      required: true,
    },
    single: {
      type: Boolean,
      default: false,
    },
    creational: {
      type: Boolean,
      default: false,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    hideSelected: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
    },
    media: {
      type: String,
      default: undefined,
    },
    optionValue: {
      type: String,
      default: 'value',
    },
    optionLabel: {
      type: String,
      default: 'label',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    closeOnSelect: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: undefined,
    },
    manualFilter: {
      type: Boolean,
      default: false,
    },
    stacked: {
      type: Boolean,
      default: false,
    },
    horizontal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      query: '',
    };
  },
  computed: {
    inputValue(): string {
      return this.isOpen ? this.query : this.getInputValue();
    },
    inputPlaceholder(): string {
      return !this.isOpen
        ? this.placeholder || 'Select some options'
        : this.getInputPlaceholder();
    },
    filteredItems(): ComboBoxItem[] {
      return this.query ? this.getFilteredItems() : this.items;
    },
    selectedItems(): ComboBoxItem[] {
      return this.value
        ? this.items.filter((item) =>
            this.value.includes(item[this.optionValue])
          )
        : [];
    },
    hasSelectedItems(): boolean {
      return this.value?.length > 0;
    },
    isNoMatchesVisible(): boolean {
      return (
        !this.loading && !this.creational && this.filteredItems.length === 0
      );
    },
    pillAttrs(): Record<string, string> {
      return {
        role: 'option',
        'aria-selected': 'true',
      };
    },
  },
  watch: {
    isOpen(value: boolean) {
      if (value) {
        createPopper(this.$refs.root, this.$refs.listbox, {
          strategy: 'fixed',
          placement: 'bottom',
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 2],
              },
            },
          ],
        });
        this.$refs.listbox.style.width = `${this.$refs.root.clientWidth}px`;
      }
    },
    loading(value: boolean) {
      if (value) {
        this.$nextTick(() => {
          const { listbox } = this.$refs;
          listbox.scrollTop = listbox.scrollHeight;
        });
      }
    },
  },
  created() {
    this.onScroll = debounce(this.onScroll, 400);
  },
  methods: {
    hide() {
      this.isOpen = false;
    },
    blur() {
      this.$refs.input.blur();
    },
    // TODO(Andrii): remove
    /* eslint-disable */
    onItemClick(item: ComboBoxItem) {
      if (this.single) {
        this.$emit('input', item[this.optionValue]);
        this.hide();
        this.blur();
      } else {
        if (this.value) {
          if (Array.isArray(this.value)) {
            if (this.value.includes(item[this.optionValue])) {
              this.$emit(
                'input',
                this.value.filter((v) => v !== item[this.optionValue])
              );
            } else {
              const array = this.value ? this.value : [];
              this.$emit('input', [...array, item[this.optionValue]]);
            }
          } else {
            if (this.value === item[this.optionValue]) {
              this.$emit('input', undefined);
            } else {
              this.$emit('input', item[this.optionValue]);
            }
          }
        } else {
          if (this.single) {
            this.$emit('input', item[this.optionValue]);
          } else {
            this.$emit('input', [item[this.optionValue]]);
          }
        }
      }

      if (this.closeOnSelect) {
        this.isOpen = false;
        this.$refs.input.blur();
      }
    },
    /* eslint-enable */
    onRemove(item: ComboBoxItem) {
      if (typeof this.value === 'string') {
        this.$emit('input', undefined);
      } else {
        this.$emit(
          'input',
          this.value.filter((i) => i !== item[this.optionValue])
        );
      }
    },
    onInput(e: InputEvent) {
      this.query = (e.target as HTMLInputElement).value;
      this.$emit('search', this.query);
    },
    onFocus() {
      this.isOpen = true;
      this.$emit('focus');
    },
    onBlur() {
      this.hide();
      this.$emit('blur');
    },
    onScroll() {
      const isBottom = isScrolledToBottom(this.$refs.listbox);
      if (isBottom) {
        this.$emit('scroll:bottom');
      }
    },
    getFilteredItems() {
      if (this.manualFilter) return this.items;
      return this.items.filter((item) =>
        item[this.optionLabel].toLowerCase().includes(this.query.toLowerCase())
      );
    },
    getOptionId(index: number) {
      return `${this.uid}-${index}`;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getItemByValue(value: any) {
      return this.items.find((item) => item[this.optionValue] === value);
    },
    isSelected(item: ComboBoxItem) {
      if (!this.value) return false;
      return this.value.includes(item[this.optionValue]);
    },
    hasMeta(item: ComboBoxItem) {
      return item.meta !== undefined;
    },
    getInputValue() {
      if (typeof this.value === 'string') {
        const item = this.getItemByValue(this.value);
        if (item) {
          return item[this.optionLabel];
        }
        return this.value;
      }
      if (!this.value) return '';
      if (this.value.length === 0) return '';
      return this.value.length === 1
        ? '1 option selected'
        : `${this.value.length} options selected`;
    },
    getInputPlaceholder() {
      if (!this.hasSelectedItems) {
        // empty text to invite the user to type something
        return '';
      }
      return this.getInputValue();
    },
    createItem() {
      const payload: ComboBoxCreateEventPayload = { text: this.query.trim() };
      this.$emit('create', payload);
      this.query = '';
      this.$refs.input.blur();
    },
    clear() {
      if (this.single) {
        this.$emit('input', undefined);
      } else {
        this.$emit('input', []);
      }
    },
  },
});
